<template>
  <div>
    <div
      class="admission-map-page"
      @click="onClickAway"
      v-if="canShowAdmissionMap()">
      <span
        class="base-title">
        {{ $t('admissionMap.title') }}
      </span>

      <v-tabs
        background-color="transparent"
        color="primary"
        v-model="tab">
        <div class="d-flex justify-space-between align-center fill-width">
          <div
            class="d-flex flex-row fill-height">
            <v-tab key="admission" href="#admission">
              {{ $t('admissionMap.tabs.admission') }}
            </v-tab>

            <v-tab key="reports" href="#reports" v-if="hasExportPermission()">
              {{ $t('admissionMap.tabs.reports') }}
            </v-tab>
          </div>

          <div
            class="primary--text d-flex flex-row align-center gap-h-5 blink-effect"
            v-if="loadingUpdate">
            <v-img
              src="icons/document-autosave-filled.svg"
              style="max-height: 22px;"
              contain
            />
            {{ $t('admissionMap.status.changesSaved') }}
          </div>
        </div>
      </v-tabs>

      <v-tabs-items
        class="transparent fill-width overflow-visible"
        v-model="tab">
        <v-tab-item
          key="admission"
          value="admission"
          class="transparent">
          <div class="d-flex flex-row my-5">
            <AdmissionMapFilter />
          </div>

          <div
            v-if="hasExportPermission()"
            class="mb-4 d-flex justify-end">
            <div
              class="d-flex flex-row gap-h-10 align-center">
              <v-switch
                :value="filter.hasRecordHistory"
                @change="setHistory"
                inset
                color="success"
                dense>
                <template v-slot:label>
                  <span
                    :class="{
                      'admission-map-text-sm': true,
                      'base-color font-weight-medium': filter.hasRecordHistory
                    }">
                    {{ $t('admissionMap.filter.withHistory') }}
                  </span>
                </template>
              </v-switch>
              <v-btn
                class="btn-component d-flex justify-space-evenly admission-map-export-btn"
                color="base-color"
                outlined
                :disabled="!hasTableResults"
                :loading="loadingExport"
                @click="exportReport">
                <v-icon
                  size="16"
                  color="base-color">
                  fas fa-download
                </v-icon>

                {{ $t('admissionMap.actions.export') }}
              </v-btn>
            </div>
          </div>

          <div class="d-flex flex-column">
            <AdmissionMapTable />
          </div>
        </v-tab-item>

        <v-tab-item
          key="reports"
          value="reports"
          class="transparent"
          v-if="hasExportPermission()">
          <div class="mt-2">
            <MyReports
              report-type="REPORT_HOSPITAL_ADMISSION_MAP"
              :reportLayoutName="$t('admissionMap.reports.layoutName')"
              :tab="tab"
              is-admission-map-report
            />
          </div>
        </v-tab-item>
      </v-tabs-items>

      <FullScreenLoader v-if="loadingPage" />

      <HistoryModal
        ref="HistoryModal"
        :history="recordHistories"
        @closed="resetHistories"
      />

      <ReportExportedModal
        ref="ReportExportedModal"
        @goToReport="goToReport"
      />
    </div>

    <SnackbarCustomize ref="AdmissionMapSnackbar" />

    <InlineError
      ref="AdmissionMapInlineError"
      :top="snackbar ? snackbar.top : null"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import AuthorityManagementMixin from '@/shared/mixins/authorityManagement/authorityManagementMixin';
import AdmissionMapFilter from '@/components/AdmissionMap/AdmissionMapFilter.vue';
import AdmissionMapTable from '@/components/AdmissionMap/AdmissionMapTable.vue';
import InlineError from '@/components/CustomAlerts/InlineError.vue';
import SnackbarCustomize from '@/components/CustomAlerts/SnackbarCustomize.vue';
import FullScreenLoader from '@/components/FullScreenLoader/FullScreenLoader.vue';
import ReportExportedModal from '@/components/InvoiceParameters/modals/ReportExportedModal.vue';
import HistoryModal from '@/components/Modals/HistoryModal/HistoryModal.vue';
import Formatters from '@/shared/formatters/formatters';
import MyReports from '../myReports/MyReports.vue';
import GenericFunctionsMixin from '@/shared/mixins/genericFunctions/genericFunctionsMixin';

export default {
  name: 'AdmissionMap',
  components: {
    ReportExportedModal,
    AdmissionMapFilter,
    AdmissionMapTable,
    FullScreenLoader,
    SnackbarCustomize,
    HistoryModal,
    InlineError,
    MyReports,
  },
  data: () => ({
    formatter: null,
    tab: 'admission',
    hasShownNoPermissionSnackbar: false,
  }),
  mixins: [ GenericFunctionsMixin, AuthorityManagementMixin ],
  computed: {
    ...mapGetters({
      recordHistories: 'admission-map/recordHistories',
      historyModal: 'admission-map/showHistoryModal',
      snackbar: 'admission-map/showSnackbarCustomize',
      loadingUpdate: 'admission-map/loadingUpdate',
      loadingExport: 'admission-map/loadingExport',
      loadingPage: 'admission-map/loadingPage',
      filter: 'admission-map/filter',
      records: 'admission-map/records',
    }),
    hasTableResults() {
      return Array.isArray(this.records) && this.records.length > 0;
    },
  },
  watch: {
    snackbar: {
      handler(snackbar) {
        if (snackbar && snackbar.show) {
          const formatter = new Formatters();
          const {
            type,
            message,
            timeout,
            top,
          } = snackbar;

          if (!formatter.isEmptyValue(top)) {
            this.$refs.AdmissionMapInlineError.open(type, message, timeout, top);
          } else {
            this.$refs.AdmissionMapSnackbar.open(type, message, timeout);
          }
        }
      },
      deep: true,
    },
    historyModal: {
      handler(historyModal) {
        if (historyModal && historyModal.show) {
          this.$refs.HistoryModal.open();
          return;
        }

        this.$refs.HistoryModal.close();
      },
      deep: true,
    },
  },
  methods: {
    ...mapMutations({
      resetRecordHistories: 'admission-map/resetRecordHistories',
      showAdmissionMapSnackbar: 'admission-map/showSnackbar',
      setIsRHProtegido: 'user-info/setIsRHProtegido',
      onClickAway: 'admission-map/onClickAway',
      setAppOrigin: 'user-info/setAppOrigin',
    }),
    ...mapActions({
      fetchPasswordStatuses: 'admission-map/fetchPasswordStatuses',
      exportAdmission: 'admission-map/exportAdmission',
      applyFilter: 'admission-map/applyFilter',
      initPage: 'admission-map/initPage',
    }),
    async exportReport() {
      const exported = await this.exportAdmission();

      if (exported) {
        setTimeout(() => {
          this.$refs.ReportExportedModal.open();
        }, 1000);
      }
    },
    goToReport() {
      this.tab = 'reports';
    },
    setHistory(hasHistory) {
      this.applyFilter({ hasRecordHistory: hasHistory });
    },
    resetHistories() {
      this.resetRecordHistories();
    },
    hasExportPermission() {
      return this.hasPermission('sdi_mapa_internacao_exportar');
    },
    hasViewPermission() {
      return this.hasPermission('sdi_mapa_internacao_visualizar');
    },
    canShowAdmissionMap() {
      const hasViewPermission = this.hasViewPermission();
      if (hasViewPermission) {
        this.hasShownNoPermissionSnackbar = false;
        return true;
      }

      if (!this.hasShownNoPermissionSnackbar) {
        this.showAdmissionMapSnackbar({
          type: 'error',
          message: String(this.$t('admissionMap.messages.noPermissionTop')),
          show: true,
        });
        this.hasShownNoPermissionSnackbar = true;
      }

      return false;
    },
  },
  async created() {
    const isRHProtegido = this.$route.query.isRHProtegido
      ? this.stringToBoolean(this.$route.query.isRHProtegido.toString())
      : this.appOrigin && this.appOrigin !== 'BACKOFFICE';

    this.setIsRHProtegido(isRHProtegido);
    this.setAppOrigin(isRHProtegido ? 'PROTECTED_HR' : 'BACKOFFICE');

    if (!this.hasViewPermission()) return;

    await this.initPage();
  },
};
</script>

<style lang="scss">
.admission-map-page {
  min-height: calc(100vh - 280px);
  margin: 20px;
}

.theme--dark .admission-map-export-btn.v-btn--disabled,
.theme--dark .admission-map-export-btn.v-btn--disabled .v-btn__content,
.theme--dark .admission-map-export-btn.v-btn--disabled .v-btn__content *,
.theme--dark .admission-map-export-btn.v-btn--disabled .v-icon,
* .theme--dark .admission-map-export-btn.v-btn--disabled,
* .theme--dark .admission-map-export-btn.v-btn--disabled .v-btn__content,
* .theme--dark .admission-map-export-btn.v-btn--disabled .v-btn__content *,
* .theme--dark .admission-map-export-btn.v-btn--disabled .v-icon {
  color: color-mix(in srgb, var(--v-foreground-base) 26%, transparent) !important;
}
</style>
